'use client'

import { Suspense, useState, useEffect } from 'react'
import { signIn } from 'next-auth/react'
import { useRouter, useSearchParams } from 'next/navigation'
import { motion, AnimatePresence } from 'framer-motion'
import { Loader2, User, Lock, ArrowRight, ShieldCheck, HelpCircle, Eye, EyeOff } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'

function parseAuthError(code: string): string {
  switch (code) {
    case 'CredentialsSignin':
      return 'Credenciales incorrectas. Verifica tu usuario y contraseña.'
    case 'AccessDenied':
      return 'Acceso denegado. Contacta al administrador.'
    default:
      return 'Ocurrió un error al iniciar sesión. Intenta de nuevo.'
  }
}

function LoginForm() {
  const router = useRouter()
  const searchParams = useSearchParams()
  const [identifier, setIdentifier] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)
  const [showPassword, setShowPassword] = useState(false)

  useEffect(() => {
    const urlError = searchParams.get('error')
    if (urlError) setError(parseAuthError(urlError))
  }, [searchParams])

  async function handleSubmit(e: React.SyntheticEvent<HTMLFormElement>) {
    e.preventDefault()
    setLoading(true)
    setError('')

    try {
      const preCheck = await fetch('/api/pre-check', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ identifier }),
      }).then(r => r.json())

      if (preCheck.status === 'expired') {
        setError('Tu acceso ha expirado. Contacta al administrador.')
        return
      }
      if (preCheck.status === 'inactive') {
        setError('Tu cuenta está desactivada. Contacta al administrador.')
        return
      }

      const res = await signIn('credentials', {
        email: identifier,
        password,
        redirect: false
      })

      if (!res?.ok || res?.error) {
        setError('Credenciales incorrectas. Verifica tu usuario y contraseña.')
      } else {
        // Solo se aceptan rutas internas como destino (evita redirecciones a otros sitios)
        const destino = new URLSearchParams(window.location.search).get('next')
        router.push(destino && destino.startsWith('/') && !destino.startsWith('//') && !destino.startsWith('/\\') ? destino : '/')
        router.refresh()
      }
    } catch (err: any) {
      setError(err?.message || 'Ocurrió un error inesperado al intentar iniciar sesión.')
    } finally {
      setLoading(false)
    }
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="space-y-2">
        <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest px-1">Acceso de Usuario</label>
        <div className="relative group">
          <div className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-300 group-focus-within:text-[#00c292] transition-colors">
            <User className="w-full h-full" />
          </div>
          <Input
            required
            value={identifier}
            onChange={(e) => setIdentifier(e.target.value)}
            placeholder="Email o Nombre de Usuario"
            className="h-14 pl-12 rounded-2xl border-slate-100 bg-slate-50/50 focus:bg-white focus:ring-4 focus:ring-[#00c292]/5 transition-all text-sm font-bold"
          />
        </div>
      </div>

      <div className="space-y-2">
        <div className="flex items-center justify-between px-1">
          <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Contraseña / Token</label>
        </div>
        <div className="relative group">
          <div className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-300 group-focus-within:text-[#00c292] transition-colors">
            <Lock className="w-full h-full" />
          </div>
          <Input
            type={showPassword ? "text" : "password"}
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="••••••••"
            className="h-14 pl-12 pr-12 rounded-2xl border-slate-100 bg-slate-50/50 focus:bg-white focus:ring-4 focus:ring-[#00c292]/5 transition-all text-sm font-bold"
          />
          <button
            type="button"
            onClick={() => setShowPassword(!showPassword)}
            className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-300 hover:text-slate-500 transition-colors"
          >
            {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {error && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="bg-rose-50 border border-rose-100 text-rose-600 p-4 rounded-2xl text-xs font-bold flex items-center gap-3 overflow-hidden"
          >
            <div className="w-5 h-5 bg-rose-100 flex items-center justify-center rounded-lg shrink-0">
              <XCircle />
            </div>
            {error}
          </motion.div>
        )}
      </AnimatePresence>

      <Button
        type="submit"
        disabled={loading}
        className="w-full h-14 rounded-2xl bg-slate-900 hover:bg-black text-white font-black uppercase text-xs tracking-widest shadow-2xl transition-all active:scale-[0.98] mt-2 group"
      >
        {loading ? (
          <Loader2 className="w-5 h-5 animate-spin" />
        ) : (
          <div className="flex items-center gap-2">
            Ingresar al Sistema
            <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
          </div>
        )}
      </Button>
    </form>
  )
}

export default function LoginPage() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-[#f0f2f5] p-6 relative overflow-hidden">
      {/* Decorative background elements */}
      <div className="absolute top-[-10%] left-[-5%] w-[400px] h-[400px] bg-[#00c292]/10 rounded-full blur-[100px]" />
      <div className="absolute bottom-[-10%] right-[-5%] w-[400px] h-[400px] bg-blue-600/10 rounded-full blur-[100px]" />

      <motion.div
        initial={{ opacity: 0, scale: 0.95, y: 10 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        transition={{ duration: 0.5, ease: 'easeOut' }}
        className="relative z-10 w-full max-w-[450px]"
      >
        <div className="bg-white rounded-[32px] shadow-[0_32px_64px_-16px_rgba(0,0,0,0.1)] overflow-hidden border border-white p-2">
          <div className="bg-white rounded-[26px] p-10 md:p-12">

            {/* Header / Logo Section */}
            <div className="flex flex-col items-center text-center mb-10">
              <motion.div
                initial={{ rotate: -10 }}
                animate={{ rotate: 0 }}
                className="w-16 h-16 bg-[#00c292] rounded-2xl flex items-center justify-center shadow-2xl shadow-green-100 mb-6 group cursor-default"
              >
                <ShieldCheck className="w-9 h-9 text-white group-hover:scale-110 transition-transform" />
              </motion.div>
              <h1 className="text-3xl font-black text-slate-800 tracking-tight">Consolidador <span className="text-[#00c292]">POS</span></h1>
              <p className="text-slate-400 font-bold text-xs uppercase tracking-[0.2em] mt-3">Plataforma de Gestión Central</p>
            </div>

            {/* Login Form wrapped in Suspense for useSearchParams */}
            <Suspense fallback={<div className="h-64" />}>
              <LoginForm />
            </Suspense>

            <div className="mt-10 pt-8 border-t border-slate-50 text-center">
              <p className="text-slate-400 text-[10px] font-bold flex items-center justify-center gap-2">
                <HelpCircle className="w-3 h-3" />
                ¿Problemas técnicos? Contacta a soporte
              </p>
            </div>

          </div>
        </div>

        {/* Footer info */}
        <p className="text-center mt-8 text-slate-400 text-[10px] font-black uppercase tracking-widest">
          © 2026 PC Soluciones • V.2.1.0
        </p>
      </motion.div>
    </div>
  )
}

function XCircle() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-circle-x"><circle cx="12" cy="12" r="10"/><path d="m15 9-6 6"/><path d="m9 9 6 6"/></svg>
  )
}
